<template>
  <b-overlay
    :show="$store.state.app.isContentLoading"
    spinner-variant="primary"
    spinner-type="grow"
    rounded="sm"
  >

    <div>

      <!-- Filters -->
      <adjustment-list-filters
        :date-filter.sync="dateFilter"
        :date-filter-value.sync="dateFilterValue"
        :clear-date.sync="clearDate"
        :branch-filter.sync="branchFilter"
        :branch-options="branchOptions"
        :reason-filter.sync="reasonFilter"
        :reason-options="reasonOptions"
        :type-filter.sync="typeFilter"
        :type-options="typeOptions"
      />

      <!-- Table Container Card -->
      <b-card
        :title="$t('Adjustments')"
        class="mb-0"
      >

        <div class="mb-2 mt-2">

          <!-- Table Top -->
          <b-row>

            <!-- Per Page -->
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start pl-0 mb-1 mb-md-0"
            >
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
              />
            </b-col>

            <!-- Search -->
            <b-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-items-center justify-content-end">
                <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  :placeholder="$t('Search')"
                  @keyup.enter="search"
                />
                <b-button
                  v-if="$can('create', 'adjustment')"
                  variant="primary"
                  @click="add"
                >
                  <span class="text-nowrap">{{ $t('New') }}</span>
                </b-button>
              </div>
            </b-col>
          </b-row>

        </div>

        <b-table
          ref="refAdjustmentListTable"
          class="position-relative"
          :items="fetchAdjustments"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          empty-text="No matching records found"
          :sort-desc.sync="isSortDirDesc"
          stacked="sm"
        >

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-link :to="{ name: 'adjustment-view', params: { id: data.item.id } }">
              <feather-icon
                icon="EyeIcon"
                size="16"
                class="align-middle text-body mr-1 cursor-pointer"
              />
            </b-link>
            <b-dropdown
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >

              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>

              <b-dropdown-item
                v-if="$can('update', 'adjustment')"
                :to="{ name: 'adjustment-edit', params: { id: data.item.id } }"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">{{ $t('Edit') }}</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="$can('delete', 'adjustment')"
              >
                <feather-icon icon="TrashIcon" />
                <span
                  class="align-middle ml-50"
                  @click="del(data.item.id)"
                >
                  {{ $t('Delete') }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>

        </b-table>
        <div class="mx-2 mb-2">
          <b-row>

            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >

              <b-pagination
                v-model="currentPage"
                :total-rows="totalAdjustments"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>
        </div>
      </b-card>

    </div>

  </b-overlay>
</template>

<script>
import {
  BOverlay, BCard, BRow, BCol, BFormInput, BButton, BTable,
  BDropdown, BDropdownItem, BPagination, BLink,
} from 'bootstrap-vue'
import vSelect from '@/libs/vue-select'
import store from '@/store'
import { onUnmounted } from '@vue/composition-api'
import AdjustmentListFilters from './AdjustmentListFilters.vue'
import storeModule from '../../../common/storeModule'
import useAdjustmentList from './useAdjustmentList'
import adjustmentStoreModule from '../adjustmentStoreModule'

export default {
  components: {
    AdjustmentListFilters,
    BOverlay,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BLink,

    vSelect,
  },
  data() {
    return {
      branchOptions: [],
      reasonOptions: [],
      tableColumns: [
        { key: 'id', label: this.$t('ID'), sortable: true },
        { key: 'adjustment_date', label: this.$t('Date'), sortable: true },
        { key: 'reason_name', label: this.$t('Reason'), sortable: true },
        { key: 'total_amount', label: this.$t('Total Amount'), sortable: true },
        { key: 'reference', label: this.$t('Reference'), sortable: true },
        { key: 'transaction_type', label: this.$t('Type'), sortable: true },
        { key: 'branch_name', label: this.$t('Branch'), sortable: true },
        { key: 'actions', label: this.$t('Actions') },
      ],
    }
  },
  watch: {
    branchFilter(newR, oldR) {
      if (newR !== null) {
        console.log(newR)
        this.reasonOptions = this.reasonOptions.filter(obj => obj.branch.includes(newR))
      } else {
        console.log(oldR)
        this.reasonOptions = JSON.parse(localStorage.reasons)
      }
    },
  },
  setup() {
    const STORE_MODULE_NAME = 'adjustment'

    // Register module
    if (!store.hasModule('common')) store.registerModule('common', storeModule)
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, adjustmentStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const typeOptions = [
      { label: 'In', value: 'in' },
      { label: 'Out', value: 'out' },
    ]

    const clearDate = null

    const {
      fetchAdjustments,
      perPage,
      currentPage,
      totalAdjustments,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refAdjustmentListTable,
      refetchData,

      // Filters
      branchFilter,
      reasonFilter,
      typeFilter,
      dateFilter,
      dateFilterValue,

    } = useAdjustmentList()

    return {

      fetchAdjustments,
      perPage,
      currentPage,
      totalAdjustments,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refAdjustmentListTable,
      refetchData,
      clearDate,
      typeOptions,

      // Extra Filters
      branchFilter,
      reasonFilter,
      typeFilter,
      dateFilter,
      dateFilterValue,

    }
  },
  mounted() {
    this.loadBranches()
    this.loadReasons()
  },
  methods: {
    loadBranches() {
      store
        .dispatch('common/fetchBranches')
        .then(response => {
          const { branches } = response.data.data
          for (let i = 0; i < branches.length; i += 1) {
            this.branchOptions.push({ label: branches[i].name, value: branches[i].id.toString() })
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
    loadReasons() {
      store
        .dispatch('common/fetchReasons')
        .then(response => {
          const { reasons } = response.data.data
          for (let i = 0; i < reasons.length; i += 1) {
            this.reasonOptions.push({ label: reasons[i].name, value: reasons[i].id.toString(), branch: reasons[i].branch })
          }
          localStorage.reasons = JSON.stringify(this.reasonOptions.value)
        })
        .catch(error => {
          console.log(error)
        })
    },
    search() {
      this.refetchData()
    },
    add() {
      this.$router.push({ name: 'adjustment-new' })
    },
    del(id) {
      const that = this
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          store.dispatch('adjustment/del', id)
            .then(response => {
              if (response.status === 200) {
                that.refetchData()
              } else {
                console.log(response)
              }
            })
            .catch(error => {
              window.swal('Error', JSON.stringify(error.response.data).replace(/"([^"]+)":/g, '$1:'))
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
